export function formatDate(date: Date): string {
  const format = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format;

  return format(date).replace(/\//g, "-");
}

export function formatDateString(dateString?: string) {
  const date = new Date(dateString ?? new Date());
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "2-digit",
  };
  const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(date);

  return formattedDate.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2 $1 $3");
}

export function formatDateTime(dateString: string) {
  const date = new Date(dateString);
  const format = new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Jakarta",
  }).format;
  const formatDate = format(date);
  const formattedDate = formatDate.replace(" at ", " ") + " WIB";
  return formattedDate;
}

export function formatDownloadDate() {
  const date = new Date();
  const format = new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const formattedDate = format.format(date);
  const downloadDate = formattedDate.replace(/\s+/g, "-");

  return downloadDate;
}

export function today() {
  const date = new Date();
  const format = new Intl.DateTimeFormat("id-ID", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });

  const formattedDate = format.format(date);

  return formattedDate;
}

export function todayMonth() {
  const date = new Date();
  const format = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });

  const formattedDate = format.format(date);

  return formattedDate;
}

/**
 * Mengubah objek Date menggunakan Intl.DateTimeFormat().formatToParts()
 * ke format string "YYYY-MM-DD HH:mm:ss.SSS" dengan TypeScript.
 * @param {Date} date - Objek Date yang akan diformat.
 * @returns {string} - String tanggal yang sudah diformat.
 */
export function formatDateWithIntl(date: Date): string {
  // 1. Tentukan opsi format dengan tipe Intl.DateTimeFormatOptions
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    hour12: false,
    timeZone: "Asia/Jakarta",
  };

  // 2. Buat formatter dan panggil .formatToParts()
  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(date);

  // 3. Ubah larik 'parts' menjadi objek. Tipenya adalah Record<string, string>
  const partsMap: Record<string, string> = Object.fromEntries(
    parts.map(({ type, value }) => [type, value]),
  );

  // 4. Susun kembali string sesuai format yang diinginkan
  return `${partsMap.year}-${partsMap.month}-${partsMap.day} ${partsMap.hour}:${partsMap.minute}:${partsMap.second}.${partsMap.fractionalSecond}`;
}

/**
 * Mengambil nilai tanggal & waktu dari string ISO dan melabelinya sebagai WIB,
 * tanpa melakukan konversi zona waktu.
 * @param {string} isoString - String tanggal, contoh: "2025-09-10T13:28:15.477+00:00".
 * @returns {string} - String tanggal dalam format WIB, contoh: "10 September 2025 13:28 WIB".
 */
export function formatIsoAsWib(isoString?: string | null): string {
  if (!isoString) return "-";

  // 1. Ambil hanya bagian tanggal dan waktu, buang info timezone
  const localString = isoString.slice(0, 23);
  const dateObject = new Date(localString);

  if (isNaN(dateObject.getTime())) {
    console.error("Input string tanggal tidak valid:", isoString);
    return "";
  }

  // 2. Siapkan opsi format, pastikan timeZoneName ada
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jakarta",
    timeZoneName: "short", // Opsi ini penting untuk memunculkan "WIB"
  };

  // 3. Gunakan .formatToParts() untuk memecah hasilnya
  const formatter = new Intl.DateTimeFormat("id-ID", options);
  const parts = formatter.formatToParts(dateObject);

  // 4. Ubah array 'parts' menjadi objek agar mudah diakses
  const partsMap: Record<string, string> = Object.fromEntries(
    parts.map(({ type, value }) => [type, value]),
  );

  // 5. Susun kembali string secara manual sesuai format yang diinginkan
  return `${partsMap.day} ${partsMap.month} ${partsMap.year} ${partsMap.hour}:${partsMap.minute} ${partsMap.timeZoneName}`;
}

/**
 * Mengubah string tanggal ISO menjadi format "YYYY-MM-DD HH:mm:ss.SSS"
 * dengan mengabaikan konversi timezone.
 * @param {string} isoString - String tanggal ISO, contoh: "2025-09-10T13:00:52.091+00:00".
 * @returns {string} - String tanggal dalam format baru, atau string kosong jika input tidak valid.
 */
/** "2026-09-23T15:37:42+07:00" -> "23-09-2026 15:37"; "-" when empty/invalid. */
export function formatGponDateTime(isoString?: string | null): string {
  if (!isoString) return "-";
  // Jam dinding apa adanya (tanpa konversi timezone). Potong di detik: slice(0, 23)
  // dulu memotong offset "+07:00" milik AFONT jadi "+07:" dan tanggalnya invalid.
  const date = new Date(String(isoString).slice(0, 19));
  if (isNaN(date.getTime())) return "-";
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .formatToParts(date)
      .map(({ type, value }) => [type, value]),
  );
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}`;
}

export function formatIsoToCustomMachineFormat(isoString: string): string {
  // 1. Potong string untuk menghapus informasi timezone (+00:00, Z, dll.)
  // Ini memastikan JavaScript menganggap waktu sebagai waktu lokal.
  const dateStringWithoutTimezone = isoString.slice(0, 23);
  const dateObject = new Date(dateStringWithoutTimezone);

  if (isNaN(dateObject.getTime())) {
    console.error("Input string tanggal tidak valid:", isoString);
    return "";
  }

  // 2. Siapkan opsi untuk mengambil semua bagian tanggal dan waktu
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    hour12: false,
  };

  // 3. Gunakan .formatToParts() untuk mengekstrak setiap komponen secara andal
  const formatter = new Intl.DateTimeFormat("en-US", options); // Locale tidak terlalu penting di sini
  const parts = formatter.formatToParts(dateObject);

  // 4. Ubah array 'parts' menjadi objek agar mudah diakses
  const partsMap: Record<string, string> = Object.fromEntries(
    parts.map(({ type, value }) => [type, value]),
  );

  // 5. Susun kembali string sesuai format yang diinginkan
  return `${partsMap.year}-${partsMap.month}-${partsMap.day} ${partsMap.hour}:${partsMap.minute}:${partsMap.second}.${partsMap.fractionalSecond}`;
}
